import {
    Box,
    Container,
    Switch,
    TextField,
    Typography,
    IconButton,
    Tooltip
} from "@mui/material"
import { useEffect, useState } from "react"
import DebugService from "../services/DebugService"
import {
    CONST_SITE_LABEL,
    STORAGE_KEYS,
    USER_ROLE,
} from "./common/Helper"
import './common/Styles.css'
import SnackbarNotification, { SNACKBAR_THEME } from "./common/SnackbarNotification"
import NotificationApiService from "../services/NotificationApiService"
import LogoLoader from "./common/contentloader/LogoLoader"
import {
    IoNotifications,
    IoMail,
    IoAdd,
    IoTrash,
    IoInformationCircleOutline,
    IoLink,
    IoSettings,
    IoPencil,
    IoCheckmark,
    IoClose
} from "react-icons/io5";
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage"
import { useAuth } from "../state/AuthContext"

/**
 * Notification Settings Component
 * @returns 
 */
export const NotificationSettings = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState(null)
    const [snackbarStatus, setSnackbarStatus] = useState(null)
    const [currentUserAccess, setCurrentUserAccess] = useState(null)
    const [notificationsEnabled, setNotificationsEnabled] = useState(false)
    const [webhooks, setWebhooks] = useState([])
    const [newWebhookUrl, setNewWebhookUrl] = useState('')
    const [editingWebhookId, setEditingWebhookId] = useState(null)
    const [editingWebhookUrl, setEditingWebhookUrl] = useState('')
    const [formData, setFormData] = useState({
        "REPORTS_COST_NOTIFICATION": {
            "EMAIL": true,
            "SLACK": true
        },
        "DASHBOARD_COST_NOTIFICATION": {
            "EMAIL": true,
            "SLACK": true
        },
        "CLOUD_COST_CONSOLE_NOTIFICATION": {
            "EMAIL": true,
            "SLACK": true
        }
    })

    const { getScopedItem } = useScopedLocalStorage()
    const { selectedAccount } = useAuth()

    const notificationList = [
        {
            title: "Reports Notifications",
            // description: "Get notified when new cost reports are generated",
            emailKey: "REPORTS_COST_NOTIFICATION_EMAIL",
            pushKey: "REPORTS_COST_NOTIFICATION_SLACK",
            showInfoIcon: false,
            infoIconDetails: ""
        },
        {
            title: "Dashboard Notifications",
            // description: "Receive updates about dashboard changes and insights",
            emailKey: "DASHBOARD_COST_NOTIFICATION_EMAIL",
            pushKey: "DASHBOARD_COST_NOTIFICATION_SLACK",
            showInfoIcon: false,
            infoIconDetails: ""
        },
        {
            title: "Anomaly Cost Detection",
            // description: "Alert when unusual spending patterns are detected",
            emailKey: "CLOUD_COST_CONSOLE_NOTIFICATION_EMAIL",
            pushKey: "CLOUD_COST_CONSOLE_NOTIFICATION_SLACK",
            showInfoIcon: true,
            infoIconDetails: "Service Cost is >= $100 and > 10% increase"
        },
    ]

    /**
     * Fetch notification settings
     */
    const fetchData = () => {
        setIsLoading(true)

        NotificationApiService.getData()
            .then(res => {
                DebugService.log(`Notification Settings Component: API response`, res)
                const apiData = res['data'] ?? {}
                setFormData(apiData)

                if (Object.keys(apiData).length !== 0) {
                    const isAllEnabled = Object.values(apiData).every(({ EMAIL, SLACK }) => EMAIL && SLACK)
                    setNotificationsEnabled(isAllEnabled)
                }

                if (res['error']) {
                    DebugService.error(`Notification Settings Component: Error caught while fetching data`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Notification Settings Component: Error caught while fetching data`, err)
                setIsLoading(false)
            })

        NotificationApiService.getWebhookList(selectedAccount)
            .then(res => {
                DebugService.log(`Notification Settings Component: Webhook data`, res)
                const apiData = res['data'] ?? []
                setWebhooks(apiData.map((d, i) => ({
                    id: d['slackIntegrationId'],
                    url: d['slackWebhookUrl']
                })))

                if (res['error']) {
                    DebugService.error(`Notification Settings Component: Error caught while fetching webhook`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Notification Settings Component: Error caught while fetching webhook`, err)
                setIsLoading(false)
            })
    }

    /**
     * Add new webhook
     */
    const addWebhook = () => {
        setSnackbarStatus('')
        setSnackbarMessage('')

        if (!newWebhookUrl.trim()) return

        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            setSnackbarStatus('failed')
            setSnackbarMessage('You do not have permission to add webhooks')
            return
        }

        setIsLoading(true)

        const requestBody = {
            slackWebhookUrl: newWebhookUrl
        }

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (selectedAccount) {
            requestBody['userAccountId'] = selectedAccount
        }

        if (parseToJson['userId']) {
            requestBody['userId'] = parseToJson['userId']
        }

        NotificationApiService.saveWebhook(requestBody)
            .then(res => {
                DebugService.log(`Notification Settings Component: Save webhook data`, res)

                if (res['error']) {
                    setSnackbarStatus('failed')
                    setSnackbarMessage(res['error']['detail'])
                    DebugService.error(`Notification Settings Component: Error caught while fetching webhook`, res['error'])
                } else {
                    setSnackbarStatus('success')
                    setSnackbarMessage(res['data'])
                    fetchData()
                    setNewWebhookUrl('')
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Notification Settings Component: Error caught while fetching webhook`, err)
                setIsLoading(false)
            })
    }

    /**
     * Delete webhook
     */
    const deleteWebhook = (webhookId) => {
        setSnackbarStatus('')
        setSnackbarMessage('')

        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            setSnackbarStatus('failed')
            setSnackbarMessage('You do not have permission to delete webhooks')
            return
        }

        setIsLoading(true)

        const requestBody = {
            slackIntegrationId: webhookId
        }

        if (selectedAccount) {
            requestBody['userAccountId'] = selectedAccount
        }

        NotificationApiService.deleteWebhook(requestBody)
            .then(res => {
                DebugService.log(`Notification Settings Component: Delete webhook response`, res)

                if (res['error']) {
                    setSnackbarStatus('failed')
                    setSnackbarMessage(res['error']['detail'])
                    DebugService.error(`Notification Settings Component: Error caught while deleting webhook`, res['error'])
                } else {
                    setSnackbarStatus('success')
                    setSnackbarMessage(res['data'])
                    fetchData()
                    setNewWebhookUrl('')
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Notification Settings Component: Error caught while deleting webhook`, err)
                setIsLoading(false)
            })
    }

    /**
     * Edit webhook
     */
    const saveEditedWebhook = () => {
        setSnackbarStatus('')
        setSnackbarMessage('')

        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            setSnackbarStatus('failed')
            setSnackbarMessage('You do not have permission to edit webhooks')
            return
        }

        setIsLoading(true)

        const requestBody = {
            slackIntegrationId: editingWebhookId,
            slackWebhookUrl: editingWebhookUrl
        }

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (selectedAccount) {
            requestBody['userAccountId'] = selectedAccount
        }

        if (parseToJson['userId']) {
            requestBody['userId'] = parseToJson['userId']
        }

        NotificationApiService.updateWebhook(requestBody)
            .then(res => {
                DebugService.log(`Notification Settings Component: Update webhook response`, res)

                if (res['error']) {
                    setSnackbarStatus('failed')
                    setSnackbarMessage(res['error']['detail'])
                    DebugService.error(`Notification Settings Component: Error caught while updating webhook`, res['error'])
                } else {
                    setSnackbarStatus('success')
                    setSnackbarMessage(res['data'])
                    fetchData()
                    setEditingWebhookId('')
                    setEditingWebhookUrl('')
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Notification Settings Component: Error caught while updating webhook`, err)
                setIsLoading(false)
            })
    }

    /**
     * Toggle master notifications
     */
    const toggleMasterNotifications = () => {
        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            return
        }

        const allEnabled = !notificationsEnabled
        setNotificationsEnabled(prev => !prev)

        const prevFormData = { ...formData }
        prevFormData['REPORTS_COST_NOTIFICATION']['EMAIL'] = allEnabled
        prevFormData['REPORTS_COST_NOTIFICATION']['SLACK'] = allEnabled
        prevFormData['DASHBOARD_COST_NOTIFICATION']['EMAIL'] = allEnabled
        prevFormData['DASHBOARD_COST_NOTIFICATION']['SLACK'] = allEnabled
        prevFormData['CLOUD_COST_CONSOLE_NOTIFICATION']['EMAIL'] = allEnabled
        prevFormData['CLOUD_COST_CONSOLE_NOTIFICATION']['SLACK'] = allEnabled

        setFormData(prevFormData)

        const requestBody = {
            settingPayload: prevFormData
        }

        saveAPICallHandler(requestBody)
    }

    /**
     * Call useEffect on page load
     */
    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Notifications`
        fetchData()

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)
        setCurrentUserAccess(parseToJson['roleName'])
    }, [])

    /**
     * API call handler
     * @param {*} requestBody 
     */
    const saveAPICallHandler = (requestBody) => {
        setSnackbarStatus(null)
        setSnackbarMessage(null)

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (parseToJson['userId']) {
            requestBody['userId'] = parseToJson['userId']
        }

        DebugService.log(`Notification Settings Component: Request to save new settings initiated`, requestBody)

        NotificationApiService.saveData(requestBody)
            .then(res => {
                DebugService.log(`Notification Settings Component: API response`, res)

                if (res['data']) {
                    setSnackbarStatus('success')
                    setSnackbarMessage(res['data']['message'])
                    setTimeout(() => {
                        fetchData()
                    }, 1000)
                }

                if (res['error']) {
                    setSnackbarStatus('failed')
                    setSnackbarMessage(res['error']['message'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.log(`Notification Settings Component: Failed to save data`, err)
                setIsLoading(false)
                setSnackbarStatus('failed')
                setSnackbarMessage('Something went wrong. Please try later')
            })
    }

    /**
     * Active toggle click handler
     * @param {*} key 
     */
    const onActiveToggleClick = (key) => {
        // Disable action for READ ONLY users
        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            return
        }

        DebugService.log(`Notification Settings Component: Request for`, key)

        const prevFormData = { ...formData }

        switch (key) {
            case 'REPORTS_COST_NOTIFICATION_EMAIL':
                prevFormData['REPORTS_COST_NOTIFICATION']['EMAIL'] = !formData['REPORTS_COST_NOTIFICATION']['EMAIL']
                break;
            case 'REPORTS_COST_NOTIFICATION_SLACK':
                prevFormData['REPORTS_COST_NOTIFICATION']['SLACK'] = !formData['REPORTS_COST_NOTIFICATION']['SLACK']
                break;
            case 'DASHBOARD_COST_NOTIFICATION_EMAIL':
                prevFormData['DASHBOARD_COST_NOTIFICATION']['EMAIL'] = !formData['DASHBOARD_COST_NOTIFICATION']['EMAIL']
                break;
            case 'DASHBOARD_COST_NOTIFICATION_SLACK':
                prevFormData['DASHBOARD_COST_NOTIFICATION']['SLACK'] = !formData['DASHBOARD_COST_NOTIFICATION']['SLACK']
                break;
            case 'CLOUD_COST_CONSOLE_NOTIFICATION_EMAIL':
                prevFormData['CLOUD_COST_CONSOLE_NOTIFICATION']['EMAIL'] = !formData['CLOUD_COST_CONSOLE_NOTIFICATION']['EMAIL']
                break;
            case 'CLOUD_COST_CONSOLE_NOTIFICATION_SLACK':
                prevFormData['CLOUD_COST_CONSOLE_NOTIFICATION']['SLACK'] = !formData['CLOUD_COST_CONSOLE_NOTIFICATION']['SLACK']
                break;
            default:
                DebugService.error(`Notification Settings Component: Invalid key received`)
                return
        }

        setFormData(prevFormData)

        const requestBody = {
            settingPayload: prevFormData
        }

        saveAPICallHandler(requestBody)
    }

    const retrieveValue = (key) => {
        switch (key) {
            case 'REPORTS_COST_NOTIFICATION_EMAIL':
                return formData['REPORTS_COST_NOTIFICATION']['EMAIL']
            case 'REPORTS_COST_NOTIFICATION_SLACK':
                return formData['REPORTS_COST_NOTIFICATION']['SLACK']
            case 'DASHBOARD_COST_NOTIFICATION_EMAIL':
                return formData['DASHBOARD_COST_NOTIFICATION']['EMAIL']
            case 'DASHBOARD_COST_NOTIFICATION_SLACK':
                return formData['DASHBOARD_COST_NOTIFICATION']['SLACK']
            case 'CLOUD_COST_CONSOLE_NOTIFICATION_EMAIL':
                return formData['CLOUD_COST_CONSOLE_NOTIFICATION']['EMAIL']
            case 'CLOUD_COST_CONSOLE_NOTIFICATION_SLACK':
                return formData['CLOUD_COST_CONSOLE_NOTIFICATION']['SLACK']
            default:
                DebugService.error(`Notification Settings Component: Invalid key received`)
                return false
        }
    }

    return (
        <>
            <div className="breadcrumb">
                Settings <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Notifications</strong>
            </div>

            <div className="page-head">
                <div>
                    <h1>Notification Settings</h1>
                    <div className="meta">Manage email and Slack notifications and webhook endpoints</div>
                </div>
            </div>

            <Container maxWidth="lg" className="notification-settings-container" disableGutters>
            {isLoading && <LogoLoader />}

            {!isLoading && (
                <>
                    {/* Master Toggle */}
                    <Box className="notification-master-toggle">
                        <Box className="notification-master-toggle-content">
                            <Box className="notification-master-toggle-label">
                                <IoNotifications className="notification-section-icon" />
                                <Box>
                                    {/* <Typography className="notification-master-toggle-text">
                                    Enable Notifications
                                </Typography> */}
                                    <Typography className="notification-master-toggle-description">
                                        Turn on/off all notification types
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                checked={notificationsEnabled}
                                onChange={toggleMasterNotifications}
                                color="primary"
                                size="medium"
                            />
                        </Box>
                    </Box>

                    {/* Email Notifications Section */}
                    <Box className="notification-section-card">
                        <Box className="notification-section-header">
                            <Typography className="notification-section-title">
                                <IoMail className="notification-section-icon" />
                                Email Notifications
                            </Typography>
                        </Box>
                        <Box className="notification-section-content">
                            {notificationList.map(({ title, description, emailKey, showInfoIcon, infoIconDetails }) => (
                                <Box key={title} className="notification-toggle-item">
                                    <Box className="notification-toggle-label">
                                        <IoSettings className="notification-toggle-icon" />
                                        <Box>
                                            <Typography className="notification-toggle-text">
                                                {title}
                                                {showInfoIcon && (
                                                    <Tooltip title={infoIconDetails}>
                                                        <IconButton size="small" className="info-icon-button">
                                                            <IoInformationCircleOutline />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Typography>
                                            {/* <Typography className="notification-toggle-description">
                                            {description}
                                        </Typography> */}
                                        </Box>
                                    </Box>
                                    <Switch
                                        checked={retrieveValue(emailKey)}
                                        onChange={() => onActiveToggleClick(emailKey)}
                                        color="primary"
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Slack Notifications Section */}
                    <Box className="notification-section-card">
                        <Box className="notification-section-header">
                            <Typography className="notification-section-title">
                                <img src="/slack.png" alt="Slack" className="notification-section-image-icon" />
                                Slack Notifications
                            </Typography>
                        </Box>
                        <Box className="notification-section-content">
                            {notificationList.map(({ title, description, pushKey, showInfoIcon, infoIconDetails }) => (
                                <Box key={title} className="notification-toggle-item">
                                    <Box className="notification-toggle-label">
                                        <IoSettings className="notification-toggle-icon" />
                                        <Box>
                                            <Typography className="notification-toggle-text">
                                                {title}
                                                {showInfoIcon && (
                                                    <Tooltip title={infoIconDetails}>
                                                        <IconButton size="small" className="info-icon-button">
                                                            <IoInformationCircleOutline />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Typography>
                                            {/* <Typography className="notification-toggle-description">
                                            {description}
                                        </Typography> */}
                                        </Box>
                                    </Box>
                                    <Switch
                                        checked={retrieveValue(pushKey)}
                                        onChange={() => onActiveToggleClick(pushKey)}
                                        color="primary"
                                        disabled={!webhooks.length}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Slack Webhooks Section */}
                    <Box className="notification-section-card">
                        <Box className="notification-section-header">
                            <Typography className="notification-section-title">
                                <img src="/slack.png" alt="Slack" className="notification-section-image-icon" />
                                Slack Webhooks
                            </Typography>
                        </Box>
                        <Box className="notification-section-content">
                            {/* Add Webhook Section */}
                            <Box className="webhook-add-section">
                                <TextField
                                    className="webhook-input-field"
                                    label="Slack Webhook URL"
                                    placeholder="https://hooks.slack.com/services/..."
                                    value={newWebhookUrl}
                                    onChange={(e) => setNewWebhookUrl(e.target.value)}
                                    variant="outlined"
                                    size="medium"
                                    disabled={currentUserAccess === USER_ROLE.READ_ONLY}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            addWebhook()
                                        }
                                    }}
                                />
                                <button
                                    className="webhook-add-button"
                                    onClick={addWebhook}
                                    disabled={!newWebhookUrl.trim() || currentUserAccess === USER_ROLE.READ_ONLY}
                                >
                                    <IoAdd />
                                    Add Webhook
                                </button>
                            </Box>

                            {/* Webhook List */}
                            <Box className="webhook-list-container">
                                {webhooks.length === 0 ? (
                                    <Box className="webhook-empty-state">
                                        <IoLink className="webhook-empty-icon" />
                                        <Typography className="webhook-empty-title">
                                            No webhooks configured
                                        </Typography>
                                        {/* <Typography className="webhook-empty-description">
                                        Add a Slack webhook URL to receive notifications in your Slack channels
                                    </Typography> */}
                                    </Box>
                                ) : (
                                    <Box className="webhook-list">
                                        {webhooks.map((webhook) => (
                                            <Box key={webhook.id} className="webhook-item">
                                                {editingWebhookId === webhook.id ? (
                                                    <>
                                                        <TextField
                                                            className="webhook-input-field"
                                                            label="Slack Webhook URL"
                                                            placeholder="https://hooks.slack.com/services/..."
                                                            value={editingWebhookUrl}
                                                            onChange={(e) => setEditingWebhookUrl(e.target.value)}
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            autoFocus
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault()
                                                                    saveEditedWebhook()
                                                                } else if (e.key === 'Escape') {
                                                                    e.preventDefault()
                                                                    setEditingWebhookId('')
                                                                    setEditingWebhookUrl('')
                                                                }
                                                            }}
                                                        />
                                                        <Box className="webhook-actions">
                                                            <button
                                                                className="webhook-save-button"
                                                                onClick={saveEditedWebhook}
                                                                disabled={currentUserAccess === USER_ROLE.READ_ONLY || !editingWebhookUrl.trim()}
                                                            >
                                                                <IoCheckmark />
                                                            </button>
                                                            <button
                                                                className="webhook-cancel-button"
                                                                onClick={() => {
                                                                    setEditingWebhookId('')
                                                                    setEditingWebhookUrl('')
                                                                }}
                                                            >
                                                                <IoClose />
                                                            </button>
                                                        </Box>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography className="webhook-url">
                                                            {webhook.url}
                                                        </Typography>
                                                        <Box className="webhook-actions">
                                                            <button
                                                                className="webhook-edit-button"
                                                                onClick={() => {
                                                                    setEditingWebhookId(webhook.id)
                                                                    setEditingWebhookUrl(webhook.url)
                                                                }}
                                                                disabled={currentUserAccess === USER_ROLE.READ_ONLY}
                                                            >
                                                                <IoPencil />
                                                            </button>
                                                            <button
                                                                className="webhook-delete-button"
                                                                onClick={() => deleteWebhook(webhook.id)}
                                                                disabled={currentUserAccess === USER_ROLE.READ_ONLY}
                                                            >
                                                                <IoTrash />
                                                            </button>
                                                        </Box>
                                                    </>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </>
            )}

            {/* Snackbar Notification */}
            {snackbarStatus && <SnackbarNotification
                initialOpen={true}
                duration={5000}
                message={snackbarMessage}
                actionButtonName={'Ok'}
                theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
                yPosition={'top'}
                xPosition={'center'}
            />}
            </Container>
        </>
    )
}